import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";

const scrollTo = (e, href) => {
  e.preventDefault();
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -72 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { lang, t, setLang } = useLang();
  const links = [
    { label: t.nav.services, href: "#services" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.testimonials, href: "#testimonials" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "backdrop-blur-xl bg-black/60 border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
        <a href="#top" onClick={(e) => scrollTo(e, "#top")} data-testid="header-logo-link" className="flex items-center gap-3 group">
          <span className="bg-white rounded-md px-2.5 py-1.5 shadow-[0_0_20px_rgba(224,18,127,0.25)] group-hover:shadow-[0_0_28px_rgba(224,18,127,0.5)] transition-shadow duration-300">
            <img src="/logo.jpg" alt="IP NET — Soluciones Prácticas" className="h-8 w-auto" />
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10" data-testid="header-nav">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => scrollTo(e, l.href)}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 border border-white/15 rounded-full p-1" data-testid="lang-toggle">
            {["en", "es"].map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                data-testid={`lang-toggle-${l}`}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1 rounded-full transition-[background-color,color] duration-300 ${
                  lang === l ? "bg-brand text-white" : "text-neutral-400 hover:text-white"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            href="#contact"
            onClick={(e) => scrollTo(e, "#contact")}
            data-testid="header-quote-button"
            className="group hidden sm:inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-white font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full transition-[background-color,transform,box-shadow] duration-300 hover:scale-105 hover:shadow-[0_0_24px_rgba(224,18,127,0.45)]"
          >
            {t.cta}
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </div>
    </motion.header>
  );
}
